import type { Feature, FeatureCollection, Point } from 'geojson';
import type { SymbolLayerSpecification } from 'mapbox-gl';
import React, { useEffect, useMemo } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';

import dropImg from '@assets/drop.png';
import pickImg from '@assets/pick.png';
import { EMap } from '@enums/map.enum';
import { StopType } from '@enums/trips.enum';
import type { TripStop } from '@store/trips/tripsSlice';

type Props = {
	markers: TripStop[];
};

const MapMarkers: React.FC<Props> = ({ markers }) => {
	const { current: map } = useMap();

	const markerFeatures = useMemo<FeatureCollection<Point>>(() => {
		const features: Feature<Point>[] = markers.map((marker) => {
			const firstPassenger = marker.passengers?.[0];
			const stopType = firstPassenger?.stopType;
			const isDrop = stopType === StopType.Drop;
			const iconName = isDrop ? StopType.Drop : StopType.Pick;

			return {
				type: EMap.Feature,
				id: marker.id,
				geometry: {
					type: EMap.Point,
					coordinates: [marker.address.lng, marker.address.lat],
				},
				properties: {
					icon: iconName,
				},
			};
		});

		return {
			type: EMap.FeatureCollection,
			features,
		};
	}, [markers]);

	const symbolLayerStyle: SymbolLayerSpecification = {
		id: EMap.markerSymbols,
		type: EMap.symbol,
		source: EMap.placeholder,
		layout: {
			[EMap.iconImage]: [EMap.get, EMap.icon],
			[EMap.iconAllowOverlap]: true,
			[EMap.iconIgnorePlacement]: true,
			[EMap.iconSize]: 0.5,
		},
	};

	const loadImages = () => {
		if (!map) {
			return;
		}

		const addImage = (name: string, url: string) => {
			if (!map.hasImage(name)) {
				const img = new Image();
				img.src = url;
				img.onload = () => {
					if (!map.hasImage(name)) {
						map.addImage(name, img);
					}
				};
			}
		};

		addImage(StopType.Pick, pickImg);
		addImage(StopType.Drop, dropImg);
	};

	useEffect(() => {
		if (map?.isStyleLoaded()) {
			loadImages();
		} else {
			map?.on(EMap.styleLoad, loadImages);
		}

		return () => {
			map?.off(EMap.styleLoad, loadImages);
		};
	}, [map]);

	return (
		<Source id={EMap.markerSymbols} type={EMap.geojson} data={markerFeatures}>
			<Layer {...symbolLayerStyle} />
		</Source>
	);
};

export default MapMarkers;
